/* eslint-disable @typescript-eslint/no-empty-function */
import type { VendorEvent } from '../../../types/global';
import type { PropsWithChildren } from 'react';

import { useRouter } from 'next/compat/router';
import { createContext, useContext, useMemo, useEffect, useRef } from 'react';

import { useConsentContext } from '../dynamic/ConsentContext';

export type DataLayerContextType = {
  pushToDataLayer: (dataLayerEvent: VendorEvent, isCookieChange?: boolean) => void;
};

const appContextDefaultValues: DataLayerContextType = {
  pushToDataLayer: () => {},
};

export const DataLayerContext = createContext<DataLayerContextType>(appContextDefaultValues);

export function useDataLayerContext() {
  return useContext(DataLayerContext);
}

export function DataLayerProvider({ children }: PropsWithChildren) {
  const router = useRouter();
  const eventsQueueRef = useRef<VendorEvent[]>([]);
  const isRouterReady = useRef(router?.isReady);
  const isSpaPushed = useRef(false);
  const previousConsentRef = useRef<boolean | undefined>(undefined);
  const { hasAnyConsent } = useConsentContext();

  // kept fresh on every render so `resetSpaPushedRef` (registered once, deps: [router]) never
  // reads a stale value from whatever render it was created in
  const hasAnyConsentRef = useRef(hasAnyConsent);

  hasAnyConsentRef.current = hasAnyConsent;

  const exhaustEventsQueue = () => {
    if (eventsQueueRef.current.length) {
      window.dataLayer ??= [];
      window.dataLayer.push(...eventsQueueRef.current);

      eventsQueueRef.current = [];
    }
  };

  const clearEventsQueue = () => {
    eventsQueueRef.current = [];
  };

  useEffect(() => {
    const handleConsentChange = () => {
      if (isSpaPushed.current && hasAnyConsent) {
        const isConsentChangeFromReject = previousConsentRef.current === false;

        if (isConsentChangeFromReject) {
          clearEventsQueue();
        } else {
          exhaustEventsQueue();
        }
      }
    };

    window.addEventListener('OneTrustGroupsUpdated', handleConsentChange);

    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', handleConsentChange);
    };
  }, [hasAnyConsent]);

  useEffect(() => {
    if (router?.isReady) {
      isRouterReady.current = true;

      if (isSpaPushed.current && hasAnyConsent) {
        const isConsentChangeFromReject = previousConsentRef.current === false;

        if (isConsentChangeFromReject) {
          clearEventsQueue();
        } else {
          exhaustEventsQueue();
        }
      }
    }

    return () => {
      isRouterReady.current = false;
    };
  }, [router?.isReady, hasAnyConsent]);

  // must run after the effects above so they compare against the *previous* consent
  // value, not the one they're reacting to - order of useEffect declarations matters here
  useEffect(() => {
    if (previousConsentRef.current !== hasAnyConsent) {
      previousConsentRef.current = hasAnyConsent;
    }
  }, [hasAnyConsent]);

  useEffect(() => {
    if (!router) {
      return () => {};
    }

    // each new page load must have its own `spa_page_load` event before dataLayer events specific
    // to that page can be pushed. Events queued on the page being left are page-scoped: with
    // consent already given, flush them now - the dataLayer still carries the outgoing page's
    // context at this point, before the URL changes - otherwise discard them, since a pending
    // or rejected user was never going to have them sent anyway.
    const resetSpaPushedRef = () => {
      isSpaPushed.current = false;

      if (hasAnyConsentRef.current) {
        exhaustEventsQueue();
      } else {
        clearEventsQueue();
      }
    };

    router.events.on('routeChangeStart', resetSpaPushedRef);

    return () => {
      router.events.off('routeChangeStart', resetSpaPushedRef);
    };
  }, [router]);

  const value = useMemo(() => {
    const pushToDataLayer = (dataLayerEvent: VendorEvent, toPush?: boolean) => {
      const canPushToDataLayer = isRouterReady.current && isSpaPushed.current;

      const shouldPushNow = toPush || (canPushToDataLayer && hasAnyConsent);

      if (shouldPushNow) {
        window.dataLayer ??= [];
        window.dataLayer.push(dataLayerEvent);

        if (dataLayerEvent.event === 'spa_page_load') {
          isSpaPushed.current = true;

          if (hasAnyConsent) {
            const isConsentChangeFromReject = previousConsentRef.current === false;

            if (isConsentChangeFromReject) {
              clearEventsQueue();
            } else {
              exhaustEventsQueue();
            }
          }
        }
      } else if (hasAnyConsent !== false) {
        // an explicit reject means these events will never be sendable - don't retain them
        eventsQueueRef.current.push(dataLayerEvent);
      }
    };

    return {
      pushToDataLayer,
    };
  }, [hasAnyConsent]);

  return <DataLayerContext.Provider value={value}>{children}</DataLayerContext.Provider>;
}
